import React, { useState, useEffect } from 'react';
import { CircularProgress } from './CircularProgress';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { 
  TrendingUp,
  Book,
  Shield,
  Award,
  Target,
  Calendar,
  Clock,
  DollarSign,
  CheckCircle,
  BarChart3,
  Flame,
  Trophy,
  GraduationCap,
  Eye,
  EyeOff
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface UserStatsProps {
  onNavigate?: (screen: any) => void; // Match setCurrentScreen type
}

export function UserStats({ onNavigate }: UserStatsProps) {
  const { user, isLoading: authLoading } = useAuth();
  // Privacy-first: earnings blurred by default
  const [earningsBlurred, setEarningsBlurred] = useState(true);
  const [educationBlurred, setEducationBlurred] = useState(false);

  // Get user profile for calculations
  const getProfileData = () => {
    const savedProfile = localStorage.getItem('clockit-profile');
    let profile = { hourlyWage: 0, payFrequency: '', workplace: '', jobTitle: '' };
    
    if (savedProfile) {
      try {
        profile = { ...profile, ...JSON.parse(savedProfile) };
      } catch (e) {
        console.warn('Could not parse saved profile');
      }
    }
    
    return profile;
  };

  const profile = getProfileData();
  const hourlyRate = profile.hourlyWage || 18.50;

  // Mock data for user stats - in real implementation, this would come from database
  const statsData = {
    thisWeek: {
      hoursWorked: 37.5,
      targetHours: 40,
      earningsProjected: 37.5 * hourlyRate,
      daysWorked: 4,
      perfectDays: 3
    },
    thisMonth: {
      hoursWorked: 142.3,
      targetHours: 160,
      earningsProjected: 142.3 * hourlyRate,
      daysWorked: 18,
      perfectDays: 14
    },
    education: {
      rightsModulesCompleted: 7,
      totalRightsModules: 12,
      knowledgeScore: 85,
      certificationsEarned: 2,
      learningStreak: 5
    },
    protection: {
      photoProofSessions: 23,
      totalSessions: 28,
      disputesResolved: 1,
      wageSavingsEstimate: 234.50
    }
  };

  const weekProgress = (statsData.thisWeek.hoursWorked / statsData.thisWeek.targetHours) * 100;
  const monthProgress = (statsData.thisMonth.hoursWorked / statsData.thisMonth.targetHours) * 100;
  const educationProgress = (statsData.education.rightsModulesCompleted / statsData.education.totalRightsModules) * 100;
  const protectionScore = (statsData.protection.photoProofSessions / statsData.protection.totalSessions) * 100;

  return (
    <div className="pb-20 bg-background min-h-screen">
      {/* Header */}
      <div className="px-4 py-6">
        <div className="clock-it-card card-content text-center">
          <h1 className="text-2xl font-bold mb-2">Your Stats</h1>
          <p className="text-caption mb-6">Track your progress and protection</p>
          
          <div className="flex justify-center mb-4">
            <CircularProgress 
              percentage={educationProgress}
              size={140}
              strokeWidth={12}
              color="#0891b2"
            >
              <div className="text-center">
                <div className="text-metric text-foreground">{statsData.education.rightsModulesCompleted}</div>
                <div className="text-caption">modules done</div>
              </div>
            </CircularProgress>
          </div>

          <div className="flex justify-center space-x-6 mb-6">
            <div className="text-center">
              <div className="w-4 h-4 rounded-full bg-success mx-auto mb-1 shadow-sm"></div>
              <div className="text-sm font-medium">{statsData.education.knowledgeScore}%</div>
              <div className="text-caption">Knowledge Score</div>
            </div>
            <div className="text-center">
              <div className="w-4 h-4 rounded-full bg-primary mx-auto mb-1 shadow-sm"></div>
              <div className="text-sm font-medium">{statsData.education.learningStreak}</div>
              <div className="text-caption">Day Streak</div>
            </div>
            <div className="text-center">
              <div className="w-4 h-4 rounded-full bg-warning mx-auto mb-1 shadow-sm"></div>
              <div className="text-sm font-medium">{statsData.education.certificationsEarned}</div>
              <div className="text-caption">Certifications</div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Cards */}
      <div className="px-4 mb-6">
        <div className="clock-it-grid clock-it-grid-2">
          {/* Weekly Progress */}
          <div className="clock-it-card card-content gradient-blue text-white">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-6 h-6" />
              <Badge className="bg-white/20 text-white border-0">
                This Week
              </Badge>
            </div>
            <div className="text-2xl font-bold mb-1">
              {statsData.thisWeek.hoursWorked}h
            </div>
            <div className="text-sm opacity-90">
              {Math.round(weekProgress)}% of {statsData.thisWeek.targetHours}h goal
            </div>
          </div>

          {/* Monthly Progress */}
          <div className="clock-it-card card-content gradient-green text-white">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-6 h-6" />
              <Badge className="bg-white/20 text-white border-0">
                This Month
              </Badge>
            </div>
            <div className="text-2xl font-bold mb-1">
              {statsData.thisMonth.hoursWorked}h
            </div>
            <div className="text-sm opacity-90">
              {Math.round(monthProgress)}% of {statsData.thisMonth.targetHours}h goal
            </div>
          </div>
        </div>
      </div>

      {/* Education Progress */}
      <div className="px-4 mb-6">
        <div className="clock-it-card card-content">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" />
                Rights Education
              </h3>
              <p className="text-xs text-muted-foreground">Build your knowledge, strengthen your protection</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onNavigate?.('rights')}
              className="text-primary hover:text-primary/80"
            >
              Continue Learning
            </Button>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{statsData.education.rightsModulesCompleted}/{statsData.education.totalRightsModules}</div>
              <div className="text-xs text-muted-foreground">Modules Complete</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-success">{statsData.education.knowledgeScore}%</div>
              <div className="text-xs text-muted-foreground">Knowledge Score</div>
            </div>
          </div>

          <div className="w-full bg-muted rounded-full h-2 mb-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${educationProgress}%` }}
            />
          </div>
          <div className="text-xs text-center text-muted-foreground">
            {Math.round(educationProgress)}% Complete
          </div>
        </div>
      </div>

      {/* Earnings & Protection Stats */}
      <div className="px-4 mb-6">
        <div className="clock-it-grid clock-it-grid-2">
          {/* Earnings Card */}
          <div className="clock-it-card card-content gradient-indigo text-white">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-6 h-6" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEarningsBlurred(!earningsBlurred)}
                className="bg-white/20 text-white hover:bg-white/30 h-auto p-1.5"
              >
                {earningsBlurred ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            <div className="text-2xl font-bold mb-1">
              {profile.hourlyWage === 0 ? '--' : 
               earningsBlurred ? '••••••' : `$${statsData.thisWeek.earningsProjected.toFixed(0)}`}
            </div>
            <div className="text-sm opacity-90">
              {profile.hourlyWage === 0 ? 'Set wage in profile' :
               earningsBlurred ? 'This week projected' : 'This week projected'}
            </div>
          </div>

          {/* Protection Score */}
          <div className="clock-it-card card-content gradient-cyan text-white">
            <div className="flex items-center justify-between mb-2">
              <Shield className="w-6 h-6" />
              <Badge className="bg-white/20 text-white border-0">
                {Math.round(protectionScore)}%
              </Badge>
            </div>
            <div className="text-2xl font-bold mb-1">
              {statsData.protection.photoProofSessions}
            </div>
            <div className="text-sm opacity-90">
              Photo-documented sessions
            </div>
          </div>
        </div>
      </div>

      {/* Achievement Cards */}
      <div className="px-4 mb-6">
        <div className="clock-it-card card-content">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-warning" />
            Recent Achievements
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                <Flame className="w-5 h-5 text-success" />
              </div>
              <div className="flex-1">
                <div className="font-medium">5-Day Learning Streak!</div>
                <div className="text-caption">You've been consistent with your education</div>
              </div>
              <Badge className="status-verified text-xs">New</Badge>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Award className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <div className="font-medium">Rights Expert</div>
                <div className="text-caption">Completed 7 educational modules</div>
              </div>
              <Badge className="status-verified text-xs">Earned</Badge>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-warning" />
              </div>
              <div className="flex-1">
                <div className="font-medium">Wage Protector</div>
                <div className="text-caption">Saved ${statsData.protection.wageSavingsEstimate} through documentation</div>
              </div>
              <Badge variant="outline" className="text-xs">Protected</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Action Cards */}
      <div className="px-4 mb-6">
        <div className="clock-it-grid clock-it-grid-3">
          <div className="clock-it-card card-content gradient-purple text-white">
            <Book className="w-6 h-6 mb-2" />
            <div className="text-lg font-bold">{statsData.education.rightsModulesCompleted}</div>
            <div className="text-xs opacity-90">Rights Learned</div>
          </div>
          
          <div className="clock-it-card card-content gradient-orange text-white">
            <Target className="w-6 h-6 mb-2" />
            <div className="text-lg font-bold">{statsData.thisWeek.perfectDays}</div>
            <div className="text-xs opacity-90">Perfect Days</div>
          </div>
          
          <div className="clock-it-card card-content gradient-teal text-white">
            <CheckCircle className="w-6 h-6 mb-2" />
            <div className="text-lg font-bold">{statsData.protection.disputesResolved}</div>
            <div className="text-xs opacity-90">Issues Resolved</div>
          </div>
        </div>
      </div>

    </div>
  );
}